import { createGlobalStyle, css, keyframes } from 'styled-components'
import { InputStyle } from 'components/form-components/styled-text-input'
import { PrimaryLink, ExternalPrimaryLink } from 'components/styled/typography'
import breakpoint from './breakpoint'
import { color, shadow } from './colors'
import font, { defaultFontNames } from './fonts'
import space from './spaces'
import RecklessBoldWoff2 from '../fonts/Reckless-Bold.woff2'
import RecklessBoldWoff from '../fonts/Reckless-Bold.woff'
import RecklessRegularWoff2 from '../fonts/Reckless-Regular.woff2'
import RecklessRegularWoff from '../fonts/Reckless-Regular.woff'

const pulse = (props: any) => keyframes`
  0% {
    transform: scale(0.95);
      box-shadow: 0 0 0 0px ${props.theme.color.primary080};
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
`

export const theme = {
  space,
  breakpoint,
  animation: {
    pulse,
  },
  mediaQuery: {
    smallPhone: `@media (max-width: ${breakpoint.xxs})`,
    phone: `@media (max-width: ${breakpoint.xs})`,
    smallTablet: `@media (max-width: ${breakpoint.ms})`,
    tablet: `@media (max-width: ${breakpoint.sm})`,
    screenS: `@media (max-width: ${breakpoint.md})`,
    screenM: `@media (max-width: ${breakpoint.lg})`,
    landscapePhone: `@media (orientation: landscape) and (max-width: ${breakpoint.sm})`,
  },
  color,
  shadow,
  font,
  size: {
    inputMinWidth: '240px',
    pageMaxWidth: '1440px',
    profileImage: {
      small: space.s40,
      smallMedium: space.s80,
      medium: space.s128,
      largeMedium: space.s320,
      large: space.s400,
    },
    pageContent: {
      xSmall: '720px',
      small: '880px',
      medium: '1040px',
      large: '1200px',
      whole: '1440px',
    },
    footerHeight: '160px',
  },
  modal: {
    xSmall: '360px',
    small: '512px',
    medium: '768px',
    large: '1024px',
  },
  header: {
    preview: '128px',
    small: '192px',
    medium: '240px',
    tall: '400px',
  },
  zIndex: {
    chatBubble: 2998,
    actionBar: 2999,
    popover: 3000,
    modal: 3001, // must be larger than alerts (3000)
    modalPopover: 3002,
  },
} as const

export type StyledTheme = typeof theme

export const GlobalStyle = createGlobalStyle<{ useCustomLink?: boolean }>`
  html {
    font-size: 62.5%;
    scroll-behavior: auto;
  }

  * {
    box-sizing: border-box;
  }

  #container {
    position: relative;
    margin: 0 auto;
    width: 100%;
  }

  input, button, select, textarea {
    font-size: ${({ theme }) => theme.space.s14};
    font-family: inherit;
}


  body {
    font-family: ${theme.font.primary};
    font-size: ${({ theme }) => theme.space.s14};
    color: ${({ theme }) => theme.color.bodyText};
    background-color: ${({ theme }) => theme.color.background};
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  .modal-open {
    overflow: hidden;
  }

  button {
    background: transparent;
    outline: none;
    border: none;
    cursor: pointer;
    user-select: none;

    &:disabled {
      cursor: not-allowed;
    }
  }

  // some 3rd party libraries might use bootstrap forms (react-datepicker)
  // so adding some form-control style override here in case it hasn't been done on the component level
  .form-control {
    ${InputStyle};
  }

  @font-face {
    font-family:"Reckless";
    font-style: normal;
    font-weight: 700;
    src:local('Reckless-Bold'),
    url(${RecklessBoldWoff2}) format("woff2"),
    url(${RecklessBoldWoff}) format("woff")
  }

  @font-face {
    font-family:"Reckless";
    font-style: normal;
    font-weight: 400;
    src:local('Reckless-regular'),
    url(${RecklessRegularWoff2}) format("woff2"),
    url(${RecklessRegularWoff}) format("woff")
  }
`

export const RelativeContainerStyle = css`
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 75%;
`

export const AbsoluteStyle = css`
  position: absolute;
  top: 0;
  left: 0;
`
export const FillStyle = css`
  width: 100%;
  height: 100%;
`

export const AbsoluteFillStyle = css`
  ${AbsoluteStyle};
  ${FillStyle};
`

export const CenterizedContentStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const GlobalBodyColor = createGlobalStyle`
  body {
    background-color: ${({ theme }) => theme.color.background};
  }
`

export const GlobalCustomLink = createGlobalStyle`
  ${ExternalPrimaryLink}, ${PrimaryLink}, .job-ad-description a {
    font-weight: 600 !important;
  }
`

export const brandDefaults = {
  headerFont: defaultFontNames.headerFont,
  bodyFont: defaultFontNames.bodyFont,
  backgroundColor: color.background,
  textColor: color.bodyText,
  headerTextColor: color.headerText,
  brandColor: color.primary110,
  buttonTextColor: color.primaryButtonText,
}
